import { z } from "zod";
import { CampaignManagedGroupSchema } from "./ai-managed-group";
import { CampaignStatusSchema, CampaignTypeSchema, TargetingTypeSchema } from "./campaign";
import { reportingDateRangeQueryShape, validateReportingDateRange } from "./reporting";

export const dashboardRangeSchema = z.enum(["today", "yesterday", "7d", "14d", "30d"]);
export type DashboardRange = z.infer<typeof dashboardRangeSchema>;

export function csvQueryValues<T extends z.ZodTypeAny>(schema: T) {
  return z.preprocess(
    (value) => {
      if (value == null || value === "") return [];
      if (Array.isArray(value)) return value.flatMap((item) => String(item).split(","));
      return String(value).split(",");
    },
    z.array(schema).transform((values) => Array.from(new Set(values))),
  );
}

/** Upper bound on a dashboard / Product Center ASIN filter selection. */
export const ASIN_FILTER_MAX = 20;

const asinFilterValueSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9]{10}$/, "Invalid ASIN");

/**
 * Optional `asins` CSV query value: child ASINs, uppercase-normalized and
 * de-duplicated, at most ASIN_FILTER_MAX. Absent or empty means "no ASIN filter".
 */
export function asinFilterQueryValues() {
  return csvQueryValues(asinFilterValueSchema)
    .refine((values) => values.length <= ASIN_FILTER_MAX, {
      message: `At most ${ASIN_FILTER_MAX} ASINs can be selected`,
    })
    .optional();
}

export const dashboardSummaryQuerySchema = z
  .object({
    range: dashboardRangeSchema.catch("30d"),
    ...reportingDateRangeQueryShape,
    // ADP-110: aggregate analytics scope — absent/empty means the legacy
    // single-shop resolution via the X-Shop-Id header.
    shopIds: csvQueryValues(z.string().min(1)),
    // Scopes ads (and, for sales-traffic, seller sales) to these child ASINs.
    asins: asinFilterQueryValues(),
  })
  .superRefine(validateReportingDateRange);
export type DashboardSummaryQuery = z.infer<typeof dashboardSummaryQuerySchema>;

export const dashboardMetricSchema = z.object({
  impressions: z.number(),
  clicks: z.number(),
  spend: z.number(),
  sales: z.number(),
  orders: z.number(),
  ctr: z.number(),
  cvr: z.number(),
  acos: z.number(),
  roas: z.number(),
  cpc: z.number(),
});
export type DashboardMetric = z.infer<typeof dashboardMetricSchema>;

export const dashboardKpiKeySchema = z.enum([
  "spend",
  "sales",
  "acos",
  "roas",
  "orders",
  "clicks",
  "impressions",
  "ctr",
  "cvr",
  "cpc",
]);
export type DashboardKpiKey = z.infer<typeof dashboardKpiKeySchema>;

export const dashboardKpiSchema = z.object({
  key: dashboardKpiKeySchema,
  label: z.string(),
  value: z.number(),
  previousValue: z.number(),
  change: z.number().nullable(),
  direction: z.enum(["up", "down", "flat"]),
  semantics: z.enum(["positive-is-good", "positive-is-bad", "neutral"]),
  format: z.enum(["currency", "percent", "number"]),
});
export type DashboardKpi = z.infer<typeof dashboardKpiSchema>;

export const dashboardCampaignSchema = z.object({
  id: z.string(),
  name: z.string(),
  // ADP-110: disambiguates the same Amazon entity across shops in the
  // aggregated list; always present (single-shop rows carry their shop too).
  shopId: z.string().default(""),
  shopName: z.string().nullable().default(null),
  campaignType: CampaignTypeSchema,
  targetingType: TargetingTypeSchema.nullable().default(null),
  bidOptimizationStrategy: z.string().nullable().default(null),
  startDate: z.string().nullable().default(null),
  status: CampaignStatusSchema,
  dailyBudget: z.number().nullable(),
  bidAiEligible: z.boolean(),
  aiManaged: z.boolean().default(false),
  aiPolicyState: z
    .enum(["managed_group", "inherited", "override", "disabled", "excluded"])
    .default("disabled"),
  managedGroup: CampaignManagedGroupSchema.nullable().default(null),
  latestAiRun: z
    .object({
      id: z.string(),
      status: z.enum(["running", "suspended", "success", "partial", "failed"]),
      startedAt: z.string(),
      candidateActions: z.number().int().nonnegative(),
      autoExecuted: z.number().int().nonnegative(),
      skipped: z.number().int().nonnegative(),
    })
    .nullable()
    .default(null),
  metrics: dashboardMetricSchema,
});
export type DashboardCampaign = z.infer<typeof dashboardCampaignSchema>;

export const dashboardAlertSchema = z.object({
  type: z.enum(["token_expired", "sync_failed", "ai_run_failed"]),
  title: z.string(),
  message: z.string(),
});
export type DashboardAlert = z.infer<typeof dashboardAlertSchema>;

export const dashboardTrendPointSchema = z.object({
  date: z.string(),
  spend: z.number(),
  sales: z.number(),
});
export type DashboardTrendPoint = z.infer<typeof dashboardTrendPointSchema>;

export const dashboardAiSummarySchema = z.object({
  managedCampaigns: z.number().int().nonnegative(),
  eligibleCampaigns: z.number().int().nonnegative(),
  enabledRate: z.number().min(0).max(1).nullable().default(null),
  spend: z.number().nonnegative().default(0),
  sales: z.number().nonnegative().default(0),
  spendShare: z.number().min(0).max(1).nullable().default(null),
  salesShare: z.number().min(0).max(1).nullable().default(null),
  pendingDecisions: z.number().int().nonnegative(),
  nextApprovalExpiresAt: z.string().datetime({ offset: true }).nullable().default(null),
  executedActions: z.number().int().nonnegative(),
  optimizedCampaigns: z.number().int().nonnegative().default(0),
  nextRunAt: z.string().nullable(),
  urgentProposal: z
    .object({
      runId: z.string(),
      campaignId: z.string(),
      campaignName: z.string(),
      actionCount: z.number().int().positive(),
    })
    .nullable(),
});
export type DashboardAiSummary = z.infer<typeof dashboardAiSummarySchema>;

export const dashboardSummaryResponseSchema = z.object({
  connected: z.boolean(),
  aiMasterOn: z.boolean().default(false),
  aiMasterOwnedByCurrentUser: z.boolean().default(false),
  range: dashboardRangeSchema,
  dataThrough: z.string().nullable().default(null),
  // ADP-110: reporting currency of the (possibly aggregated) money values.
  // `currencyApproximate` marks converted mixed-currency aggregates (≈USD);
  // `excludedShops` counts scope shops left out (disconnected / unresolvable).
  currency: z.string().nullable().default(null),
  currencyApproximate: z.boolean().default(false),
  excludedShops: z.number().int().nonnegative().default(0),
  kpis: z.array(dashboardKpiSchema),
  trend: z.array(dashboardTrendPointSchema).default([]),
  aiSummary: dashboardAiSummarySchema.default({
    managedCampaigns: 0,
    eligibleCampaigns: 0,
    enabledRate: null,
    spend: 0,
    sales: 0,
    spendShare: null,
    salesShare: null,
    pendingDecisions: 0,
    nextApprovalExpiresAt: null,
    executedActions: 0,
    optimizedCampaigns: 0,
    nextRunAt: null,
    urgentProposal: null,
  }),
  campaigns: z.array(dashboardCampaignSchema),
  alerts: z.array(dashboardAlertSchema),
});
export type DashboardSummaryResponse = z.infer<typeof dashboardSummaryResponseSchema>;

// ── Campaign analytics (ADP-102) ────────────────────────────────────────────

export const campaignAnalyticsGranularitySchema = z.enum(["day", "week", "month"]);
export type CampaignAnalyticsGranularity = z.infer<typeof campaignAnalyticsGranularitySchema>;

export const campaignAnalyticsPolicyStateSchema = z.enum([
  "managed_group",
  "inherited",
  "override",
  "disabled",
  "excluded",
]);
export type CampaignAnalyticsPolicyState = z.infer<typeof campaignAnalyticsPolicyStateSchema>;

export const campaignAnalyticsRunGroupSchema = z.enum([
  "attention",
  "completed",
  "analyzing",
  "never",
]);
export type CampaignAnalyticsRunGroup = z.infer<typeof campaignAnalyticsRunGroupSchema>;

export const campaignAnalyticsQuerySchema = z
  .object({
    campaignId: z.string().uuid().optional(),
    range: dashboardRangeSchema.default("30d"),
    ...reportingDateRangeQueryShape,
    granularity: campaignAnalyticsGranularitySchema.default("day"),
    delivery: z.enum(["all", "enabled", "paused", "archived"]).default("all"),
    search: z.string().trim().max(200).default(""),
    policy: csvQueryValues(campaignAnalyticsPolicyStateSchema),
    run: csvQueryValues(campaignAnalyticsRunGroupSchema),
    // ADP-110: aggregate analytics scope (absent/empty → legacy single-shop).
    shopIds: csvQueryValues(z.string().min(1)),
    asins: asinFilterQueryValues(),
  })
  .superRefine(validateReportingDateRange);
export type CampaignAnalyticsQuery = z.infer<typeof campaignAnalyticsQuerySchema>;

export const campaignAnalyticsKpiKeySchema = z.enum([
  "spend",
  "sales",
  "orders",
  "acos",
  "cpc",
  "cpa",
  "roas",
  "clicks",
  "impressions",
  "ctr",
  "cvr",
]);
export type CampaignAnalyticsKpiKey = z.infer<typeof campaignAnalyticsKpiKeySchema>;

export const campaignAnalyticsKpiSchema = z.object({
  key: campaignAnalyticsKpiKeySchema,
  label: z.string(),
  value: z.number().nullable(),
  previousValue: z.number().nullable(),
  change: z.number().nullable(),
  direction: z.enum(["up", "down", "flat"]),
  semantics: z.enum(["positive-is-good", "positive-is-bad", "neutral"]),
  format: z.enum(["currency", "percent", "number"]),
});
export type CampaignAnalyticsKpi = z.infer<typeof campaignAnalyticsKpiSchema>;

/**
 * Metric values are flattened rather than nested so a selected KPI key can index
 * a trend point directly (`point[kpi.key]`) on both the chart and the data table.
 */
export const campaignAnalyticsTrendPointSchema = z.object({
  periodStart: z.string().date(),
  periodEnd: z.string().date(),
  spend: z.number().nullable(),
  sales: z.number().nullable(),
  orders: z.number().nullable(),
  acos: z.number().nullable(),
  cpc: z.number().nullable(),
  cpa: z.number().nullable(),
  roas: z.number().nullable(),
  clicks: z.number().nullable(),
  impressions: z.number().nullable(),
  ctr: z.number().nullable(),
  cvr: z.number().nullable(),
  dataStatus: z.enum(["complete", "partial", "missing"]),
});
export type CampaignAnalyticsTrendPoint = z.infer<typeof campaignAnalyticsTrendPointSchema>;

export const campaignAnalyticsResponseSchema = z.object({
  connected: z.boolean(),
  range: dashboardRangeSchema,
  granularity: campaignAnalyticsGranularitySchema,
  dataThrough: z.string().date().nullable(),
  matchedCampaigns: z.number().int().nonnegative(),
  kpis: z.array(campaignAnalyticsKpiSchema),
  trend: z.array(campaignAnalyticsTrendPointSchema),
  // ADP-110: see dashboardSummaryResponseSchema.
  currency: z.string().nullable().default(null),
  currencyApproximate: z.boolean().default(false),
  excludedShops: z.number().int().nonnegative().default(0),
});
export type CampaignAnalyticsResponse = z.infer<typeof campaignAnalyticsResponseSchema>;
